import React, { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../../../style/Dentisstyle.css";
import { apiAxios } from "../../../../utils/apiAxios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface Clinic {
  clinicID: number;
  clinicName: string;
  address: string;
  deliveryContact: string;
  emailID?: string;
  gstNo: string;
  practiceID: string;
  isApprove: boolean;
  isReject: boolean;
  isActive: boolean;
  createdDate: string;
}

const DentistApproval: React.FC = () => {
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [filteredClinics, setFilteredClinics] = useState<Clinic[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const dataWithIndex = filteredClinics.map((item: any, index: number) => ({
    ...item,
    index
  }));

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingClinicID, setDeletingClinicID] = useState<number | null>(null);

  const token = sessionStorage.getItem("token") || "";

  const fetchClinics = async () => {
    try {
      setLoading(true);

      const res = await apiAxios.get(
        `${API_BASE}/ClinicDetails/ClinicAll`
      );

      if (res.data.isSuccess && res.data.data) {

        const sortedClinics = res.data.data.sort(
          (a: Clinic, b: Clinic) =>
            new Date(b.createdDate).getTime() -
            new Date(a.createdDate).getTime()
        );


        setClinics(sortedClinics);
        setFilteredClinics(sortedClinics);
      } else {

        setClinics([]);
        setFilteredClinics([]);
      }
    } catch (error) {
      console.error("Fetch clinics error:", error);
      toast.error("Failed to fetch clinics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClinics();
  }, []);

  // Filter clinics based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredClinics(clinics);
    } else {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = clinics.filter(clinic =>
        clinic.clinicName.toLowerCase().includes(lowercasedSearch) ||
        clinic.address.toLowerCase().includes(lowercasedSearch) ||
        clinic.deliveryContact.toLowerCase().includes(lowercasedSearch) ||
        clinic.gstNo.toLowerCase().includes(lowercasedSearch) ||
        clinic.practiceID.toLowerCase().includes(lowercasedSearch) ||
        clinic.clinicID.toString().includes(lowercasedSearch)
      );
      setFilteredClinics(filtered);
    }
  }, [searchTerm, clinics]);

  const handleApprove = async (clinicID: number) => {
    try {
      const res = await apiAxios.post(
        `${API_BASE}/ClinicDetails/Approve/${clinicID}`,
        {},
        { headers: { AuthorizationData: token } }
      );
      // if (res.data.isSuccess) {
      //   toast.success("Clinic approved successfully!");
      //   setShowModal(false);
      //   fetchClinics();
      // } 
      if (res.data.isSuccess) {
        toast.success("Clinic approved successfully!");
        setShowModal(false);

        // 🔥 1. Update sessionStorage user data
        const userStr = sessionStorage.getItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);

          if (Array.isArray(user.clinicDetails)) {
            user.clinicDetails = user.clinicDetails.map((c: any) =>
              c.clinicID === clinicID
                ? { ...c, isApprove: true, isReject: false }
                : c
            );

            sessionStorage.setItem("user", JSON.stringify(user));
          }
        }

        // 🔥 2. Notify DentistLayout instantly (NO refresh)
        window.dispatchEvent(new Event("clinicApproved"));

        // 🔁 Keep admin list updated
        fetchClinics();
      }

      else {
        toast.warning(res.data.status || "Unable to approve clinic.");
      }
    } catch {
      toast.error("Error approving clinic.");
    }
  };

  const openRejectModal = (clinic: Clinic) => {
    setSelectedClinic(clinic);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please enter a reason for rejection");
      return;
    }

    if (!selectedClinic) {
      toast.error("No clinic selected");
      return;
    }

    try {
      const res = await apiAxios.post(
        `${API_BASE}/ClinicDetails/Reject/${selectedClinic.clinicID}`,
        { RejectReason: rejectReason },
        { headers: { AuthorizationData: token } }
      );

      if (res.data.isSuccess) {
        toast.info("Clinic rejected successfully!");
        setShowRejectModal(false);
        setShowModal(false);
        fetchClinics();
      } else {
        toast.warning(res.data.status || "Unable to reject clinic.");
      }
    } catch (error) {
      console.error("Reject clinic error:", error);
      toast.error("Error rejecting clinic.");
    }
  };

  const handleDeleteClick = (clinicID: number) => {
    setDeletingClinicID(clinicID);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deletingClinicID) return;

    try {
      const res = await apiAxios.post(
        `${API_BASE}/ClinicDetails/Delete/${deletingClinicID}`,
        {},
        { headers: { AuthorizationData: token } }
      );

      if (res.data.isSuccess) {
        toast.success("Clinic deleted (marked inactive) successfully!");
        fetchClinics();
      } else {
        toast.warning(res.data.status || "Unable to delete clinic.");
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.status ||
        error?.response?.data?.Status ||
        error?.response?.data?.message ||
        "Error deleting clinic";

      toast.error(message);
    } finally {
      setShowDeleteConfirm(false);
      setDeletingClinicID(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletingClinicID(null);
  };

  const getStatusBadge = (clinic: Clinic) => {
    if (!clinic.isActive) return <span className="badge bg-secondary">Inactive</span>;
    if (clinic.isApprove) return <span className="badge bg-success approved-btnpadding">Approved</span>;
    if (clinic.isReject) return <span className="badge bg-danger approved-btnpadding">Rejected</span>;
    return <span className="badge bg-warning text-white approved-btnpadding">Pending</span>;
  };

  const columns: TableColumn<Clinic>[] = [
    {
      name: "Clinic ID",
      cell: (row) =>
        row.isActive && !row.isApprove && !row.isReject ? (
          <a
            onClick={() => {
              setSelectedClinic(row);
              setShowModal(true);
            }}
            className="tobe-verify-text"
          >
            To Be Verified
          </a>
        ) : (
          <span>{row.clinicID}</span>
        ),
      minWidth: "140px",
      sortable: true,
      center: true,
    },
    {
  name: "Clinic Name",
  selector: (row) => row.clinicName,
  minWidth: "180px",
  sortable: true,
  cell: (row) => (
    <span title={row.clinicName}>
      {row.clinicName}
    </span>
  ),
},
{
  name: "Address",
  minWidth: "160px",
  sortable: true,
  cell: (row) => (
    <div
      title={row.address}
      style={{
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "150px",
      }}
    >
      {row.address}
    </div>
  ),
},
    { name: "Delivery Contact", selector: (row) => row.deliveryContact, center: true, minWidth: "130px", sortable: true },
    { name: "GST No", selector: (row) => row.gstNo || "-", minWidth: "80px", center: true, sortable: true },
    { name: "Practice ID", selector: (row) => row.practiceID || "-", center: true, minWidth: "80px", sortable: true },
    { name: "Status", cell: (row) => getStatusBadge(row), center: true, minWidth: "80px", sortable: true },
    {
      name: "Actions",
      cell: (row) =>
        row.isActive ? (
          <button
            onClick={() => handleDeleteClick(row.clinicID)}
            className="action-btn delete"
            title="Delete"
          >
            <i className="bi bi-trash customdelete-icon"></i>
          </button>
        ) : (
          <span className="text-muted"></span>
        ),
      center: true,
      minWidth: "90px"
    },
  ];

  const customStyles = {

    // headRow: { style: { backgroundColor: "#3D8BFD", color: "white" } },
    headCells: { style: { fontSize: "14px", justifyContent: "center" } },
    cells: { style: { fontSize: "14px" } },

  };

  return (
    <div className="min-h-screen">
      <div className="premium-card">
        <h4 className="mb-4">Dentist Approval</h4>

        {/* Search Bar */}
        <div className="row mb-3">
          <div className="col-md-6 col-12 col-lg-3">
            <div className="input-group position-relative">
              <input
                type="text"
                className="form-control"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              <button
                className="btn btn-primary"
                type="button"
                onClick={() => setSearchTerm("")}
              >
                <i className="bi bi-search"></i>
              </button>
            </div>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={dataWithIndex}
          progressPending={loading}
          pagination
          highlightOnHover
          pointerOnHover
          onRowClicked={(row) => {
            setSelectedClinic(row);
            setShowModal(true);
          }}
          noDataComponent={
            searchTerm
              ? `No clinics found for "${searchTerm}"`
              : "No clinic records found."
          }
          className="admin-delivery-table"
        />


      </div>

      {/* Approval Modal */}
      {showModal && selectedClinic && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{ width: "100%", maxWidth: "600px", margin: "auto" }}>
              <div className="modal-header closebtn-and-deletebtndent text-white">

                <h5 className="modal-title customdelete-title">
                  Clinic Details — ClinicID {selectedClinic.clinicID}
                </h5>

                {/* <button className="close-icon popup-closeicondentist" onClick={() => setShowModal(false)}>
      <i className="bi bi-x-lg"></i>

    </button> */}

                {/* <button
                  className="close-icon popup-closeicondentist"
                  onClick={() => setShowModal(false)}
                >
                  <i className="bi bi-x-lg"></i>
                </button> */}


                <button
                  type="button"
                  className="btn-close custom-close-btn"
                  data-bs-dismiss="modal"
                  aria-label="Close" onClick={() => setShowModal(false)}
                ><i className="bi bi-x customcross"></i></button>

              </div>

              <div className="modal-bodyclinic dentist-modalbody">
                <ul className="clinic-verificationlist">
                  <li className="clinic-namefontweight">Clinic Name</li>
                  <li>{selectedClinic.clinicName}</li>
                </ul>
                <ul className="clinic-verificationlist">
                  <li className="clinic-namefontweight">Address</li>
                  <li className="address-name-section">{selectedClinic.address}</li>
                </ul>
                <ul className="clinic-verificationlist">
                  <li className="clinic-namefontweight">Delivery Contact</li>
                  <li>{selectedClinic.deliveryContact}</li>
                </ul>
                <ul className="clinic-verificationlist">
                  <li className="clinic-namefontweight">Email ID</li>
                  <li>{selectedClinic.emailID || "-"}</li>
                </ul>
                <ul className="clinic-verificationlist">
                  <li className="clinic-namefontweight">GST No</li>
                  <li>{selectedClinic.gstNo || "-"}</li>
                </ul>
                {/* <ul className="clinic-verificationlist">
                  <li className="clinic-namefontweight">Practice ID</li>
                  <li>{selectedClinic.practiceID || "-"}</li>
                </ul> */}

                {/* <ul className="clinic-verificationlist">
                  <li className="clinic-namefontweight">Created Date</li>
                  <li>
                    {selectedClinic.createdDate
                      ? new Date(selectedClinic.createdDate).toLocaleDateString('en-GB')
                      : "-"
                    }
                  </li>
                </ul> */}
                <ul className="clinic-verificationlist">
                  <li className="clinic-namefontweight">Status</li>
                  <li>{getStatusBadge(selectedClinic)}</li>
                </ul>
              </div>

              <div className="mt-2 py-3 btn-sectionfooter">
                {!selectedClinic.isApprove && !selectedClinic.isReject && selectedClinic.isActive && (
                  <>
                    <button onClick={() => handleApprove(selectedClinic.clinicID)} className="btn-primary py-2 btn">Approve</button>
                    <button onClick={() => openRejectModal(selectedClinic)} className="btn-secondary mx-2 py-2 btn">Reject</button>
                  </>
                )}
                {(!selectedClinic.isActive || selectedClinic.isApprove || selectedClinic.isReject) && (
                  <button className="btn btn-secondary py-2 " onClick={() => setShowModal(false)}>Close</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">

              <div className="modal-header closebtn-and-deletebtndent text-white">
                <h5 className="modal-title customdelete-title ">Reject Clinic</h5>

                <button type="button" onClick={() => setShowRejectModal(false)} className="btn-close custom-close-btn" data-bs-dismiss="modal" aria-label="Close"><i className="bi bi-x customcross"></i></button>

              </div>
              <div className="modal-body">
                <label className="form-label ">Enter reason for rejection</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Type your rejection reason..."
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={confirmReject}>Confirm Reject</button>

                <button className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {/* {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="custom-modal modal">
            <div className="closebtn-and-deletebtn">
              <div className="modal-title customdelete-title">Delete Clinic</div>
              <div className="clinic-deleteicon" onClick={cancelDelete}>
                <i className="bi bi-x-lg"></i>
              </div>
            </div>
            <p className="py-2">
              Are you sure you want to delete this clinic? <br />
              This action cannot be undone.
            </p>
            <div className="modal-buttons">
              <button onClick={confirmDelete} className="btn-primary btn py-2 px-3">Delete</button>
              <button onClick={cancelDelete} className="btn-secondary btn py-2 px-3">Cancel</button>
            </div>
          </div>
        </div>
      )} */}
      {showDeleteConfirm && (
        <>
          <div className="modal-backdrop fade show"></div>

          <div
            className="modal fade show"
            tabIndex={-1}
            style={{ display: "block" }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog ">
              <div className="modal-content">

                <div className="modal-header">
                  <h5 className="modal-title ">Delete Clinic</h5>
                  {/* <button
              type="button"
              className="btn-close custom-close-btn"
              onClick={cancelDelete}
            ></button> */}


                  <button
                    type="button"
                    className="btn-close custom-close-btn"
                    data-bs-dismiss="modal"
                    aria-label="Close" onClick={cancelDelete}
                  ><i className="bi bi-x customcross"></i></button>
                </div>

                <div className="modal-body">
                  <div className="text-center">
                    Are you sure you want to delete this clinic?
                    <br />
                    <strong>This action cannot be undone.</strong>
                  </div>
                </div>

                <div className="d-flex justify-content-center pb-3 gap-2">
                  <button onClick={confirmDelete} className="btn-primary btn">Delete</button>
                  <button onClick={cancelDelete} className="btn-secondary btn">Cancel</button>

                </div>

              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DentistApproval;